import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { DataTablePFSEvent } from "primereact/datatable";
import { useCallback, useState } from "react";
import { DCSQueryClient } from "../../../queryClient";
import { If } from "../../../components/If/If";
import { UserRoles } from "../../../constants/userRoles";
import { useFilterQuery } from "../../../hooks/useFilterQuery";
import { useToast } from "../../../Providers/ToastProvider";
import { PrivateAccess } from "../../../keycloak";
import { requests } from "../../../services/http-common";
import { IFilter } from "../../../types";
import { DeleteItemPopup } from "../components/DeleteItemPopup/DeleteItemPopup";
import { ItemList } from "../components/ItemList/ItemList";
import { SetItemPopup } from "../components/SetItemPopup/SetItemPopup";
import { TItem } from "../components/types";
import classes from "./tabs.module.scss";
import { useEffectWithoutMount } from "../../../helpers/helperHooks";

const defaultFilters = {
  first: 0,
  rows: 10,
  page: 1,
  filter: "",
  filterByStatus: "",
  filterByCustomerCategory: "",
  sortOrder: -1,
};

export const DenyReasonTypes = () => {
  const [filters, setFilters] = useState<IFilter>(defaultFilters as IFilter);
  const [item, setItem] = useState<TItem | null>(null);
  const [itemDialog, setItemDialog] = useState(false);
  const [deleteItemDialog, setDeleteItemDialog] = useState(false);

  const filterQuery = useFilterQuery(filters);
  const { show } = useToast();

  const { data, refetch } = useQuery({
    queryKey: ["deny-reason-types"],
    queryFn: () => {
      return requests.get(`/denied-reason-type?${filterQuery}`);
    },
  });

  const addItem = useMutation({
    mutationFn: (name: string) => {
      return requests.post("/denied-reason-type", { name });
    },
    onSuccess: () => {
      hideItemDialog();
      show({ detail: "Item added successfully !" });
      DCSQueryClient.invalidateQueries({
        queryKey: ["deny-reason-types"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const editItemRequest = useMutation({
    mutationFn: ({ name, id }: TItem) => {
      return requests.put(`/denied-reason-type/${id}`, { name });
    },
    onSuccess: () => {
      hideItemDialog();
      show({ detail: "Item updated successfully !" });
      DCSQueryClient.invalidateQueries({
        queryKey: ["deny-reason-types"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const deleteItem = useMutation({
    mutationFn: ({ id }: TItem) => {
      return requests.delete(`/denied-reason-type/${id}`);
    },
    onSuccess: () => {
      setDeleteItemDialog(false);
      DCSQueryClient.invalidateQueries({
        queryKey: ["deny-reason-types"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const handleSetItem = (item: { name: string; id: string | null }) => {
    if (item.id) {
      editItemRequest.mutate(item as TItem);
      return;
    }
    addItem.mutate(item.name);
  };

  const onPage = useCallback(
    (event: DataTablePFSEvent) => {
      setFilters((currVal: any) => ({
        ...currVal,
        first: event.first,
        rows: event.rows,
        page: event.page,
      }));
    },
    [setFilters]
  );

  useEffectWithoutMount(() => {
    refetch();
  }, [filters, refetch]);

  const editItem = (item: TItem) => {
    setItem(item);
    setItemDialog(true);
  };

  const confirmDeleteItem = (item: TItem) => {
    setItem(item);
    setDeleteItemDialog(true);
  };

  const hideDeleteItemDialog = () => {
    setDeleteItemDialog(false);
  };

  const hideItemDialog = () => {
    setItemDialog(false);
    setItem(null);
  };

  const handleAddItem = () => {
    setItem(null);
    setItemDialog(true);
  };

  const cardTitle = () => {
    return (
      <div className={classes.cardTitle}>
        <h4>Deny reason types</h4>
        <PrivateAccess
          roles={{
            dcs_client: [UserRoles.DENIED_REASON_TYPE_CREATE],
          }}
        >
          <Button
            icon="pi pi-plus"
            className=""
            label="New"
            onClick={handleAddItem}
          />
        </PrivateAccess>
      </div>
    );
  };

  return (
    <div className={classes.container}>
      <Card title={cardTitle}>
        <ItemList
          totalRecords={data?.total}
          records={data?.result}
          first={filters.first}
          rows={filters.rows}
          onPage={onPage}
          rowActionDelete={confirmDeleteItem}
          rowActionEdit={editItem}
        />
      </Card>
      <If condition={deleteItemDialog}>
        <DeleteItemPopup
          item={item}
          onHide={hideDeleteItemDialog}
          onConfirm={deleteItem.mutate}
          deleteRequestPending={deleteItem.isLoading}
        />
      </If>
      <If condition={itemDialog}>
        <SetItemPopup
          item={item}
          onHide={hideItemDialog}
          onSubmit={handleSetItem}
        />
      </If>
    </div>
  );
};
